import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import QuoteForm from "../components/QuoteForm";
import { fetchProducts } from "../lib/api";
import { useLocation } from "react-router-dom";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("");
  const location = useLocation();

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      const slug = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(slug);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [location, products]);

  const selected = products.find((p) => p.slug === active);

  return (
    <div data-testid="products-page" className="bg-[var(--bg-primary)]">
      <Navbar />

      {/* HEADER */}
      <section className="pt-36 pb-16 bg-[var(--maroon-950)] text-white relative grain">
        <div className="container-edge relative">
          <div className="overline text-[#D38888]">Catalog</div>
          <h1 className="font-display text-5xl md:text-7xl mt-5 leading-[1] tracking-tight">
            Products & Services
          </h1>
          <p className="mt-6 max-w-2xl text-[#E8DCC8]/85 text-lg leading-relaxed">
            Six core families of biscuit dies and rollers — each available in standard sizes, and almost always made bespoke
            to your line, your dough and your shelf.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="section">
        <div className="container-edge">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {products.map((p, i) => (
              <div key={p.slug} id={p.slug}>
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS LIST */}
      <section className="bg-[var(--bg-secondary)] section">
        <div className="container-edge">
          <div className="overline">Specifications</div>
          <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight max-w-3xl">
            What goes into every piece we ship.
          </h2>

          <div className="mt-16 space-y-px bg-[var(--border-subtle)] border border-[var(--border-subtle)]">
            {products.map((p, i) => (
              <article
                key={p.slug}
                data-testid={`product-detail-${p.slug}`}
                className="bg-[var(--bg-primary)] p-7 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-8"
              >
                <div className="md:col-span-1 font-mono-accent text-[var(--maroon-700)] text-sm">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-display text-2xl md:text-3xl text-[var(--maroon-950)] leading-tight">{p.name}</h3>
                  <p className="mt-2 italic font-display text-[var(--maroon-700)]">{p.tagline}</p>
                </div>
                <div className="md:col-span-7">
                  <p className="text-[var(--text-secondary)] leading-relaxed">{p.description}</p>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[15px]">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[var(--text-secondary)]">
                        <span className="text-[var(--maroon-700)] mt-1">▸</span> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`product-detail-cta-${p.slug}`}
                    onClick={() => {
                      setActive(p.slug);
                      document.getElementById("quote-anchor")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="btn-secondary mt-7"
                  >
                    Request a quote for {p.name}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section id="quote-anchor" className="section">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">Request a quote</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
              Tell us your line. We'll spec the tooling.
            </h2>
            <p className="mt-6 text-[var(--text-secondary)] leading-relaxed">
              Share your biscuit type, line make, and any drawings or samples you have. Our team responds within one business day.
            </p>
          </div>
          <div className="md:col-span-7">
            <QuoteForm defaultProduct={selected ? selected.name : ""} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
